import { useEffect, useState } from "react";
import { Warning, ArrowBendUpRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Escalations() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/escalations")).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${active.id}/escalate`, { note });
      toast.success("Escalation sent to reviewer");
      setActive(null); setNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader overline="SLA Management" title="Escalation Inbox">
        Every review that has breached its SLA window. Escalate to nudge the assigned reviewer and log the action.
      </PageHeader>

      {rows.length === 0 ? (
        <EmptyState title="No breached reviews" subtitle="All reviews are within their SLA windows. Nice work." />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contract</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Reviewer</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Overdue</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`escalation-row-${r.id}`}>
                  <TableCell>
                    <div className="font-medium text-foreground">{r.title}</div>
                    <div className="text-xs text-muted-foreground">{r.contract_number} · {money(r.amount)}</div>
                  </TableCell>
                  <TableCell className="text-sm">{r.stage_label}</TableCell>
                  <TableCell className="text-sm">{r.reviewer_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.due_at}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-alert/10 text-alert px-2 py-0.5 rounded-sm">
                      <Warning size={12} weight="fill" /> {r.days_overdue}d
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {r.escalation ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={14} weight="fill" /> Escalated</span>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`escalate-btn-${r.id}`} onClick={() => setActive(r)}>
                        <ArrowBendUpRight size={14} className="mr-1" /> Escalate
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Escalate review</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Notify the assigned reviewer for <b>{active?.title}</b> ({active?.reviewer_name}).
          </p>
          <Textarea data-testid="escalation-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the reviewer…" />
          <DialogFooter>
            <Button data-testid="escalation-submit" onClick={submit} disabled={busy}>{busy ? "Sending…" : "Send escalation"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
